// javascript
// Patches <a> elements inside the chat messages container so they don't get
// trapped inside a <turbo-frame>:
//   - internal (same-origin) links → data-turbo-frame="_top" (full navigation)
//   - external links              → target="_blank" rel="noopener noreferrer"
//
// Also runs on dynamically added content (Turbo broadcasts) via MutationObserver.
// Idempotent: links already patched are marked with data-link-patched="true".
//
// No infinite-loop risk: MutationObserver watches childList only (not attributes).
// Setting data-turbo-frame / target / rel on links is an attribute mutation that
// does NOT trigger childList mutations.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this._patchAll()
    this.observer = new MutationObserver((mutations) => {
      const hasNewNodes = mutations.some((m) => m.addedNodes.length > 0)
      if (hasNewNodes) this._patchAll()
    })
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  _patchAll() {
    const links = this.element.querySelectorAll("a[href]:not([data-link-patched])")
    links.forEach((link) => this._patch(link))
  }

  _patch(link) {
    link.setAttribute("data-link-patched", "true")

    const href = link.getAttribute("href")
    if (!href || href.startsWith("#") || href.startsWith("javascript:")) return

    if (this._isExternal(href)) {
      if (!link.hasAttribute("target")) link.setAttribute("target", "_blank")
      if (!link.hasAttribute("rel")) link.setAttribute("rel", "noopener noreferrer")
    } else if (!link.hasAttribute("data-turbo-frame")) {
      // Same-origin: break out of any enclosing turbo-frame.
      link.setAttribute("data-turbo-frame", "_top")
    }
  }

  _isExternal(href) {
    if (href.startsWith("/")) return false
    try {
      const url = new URL(href, window.location.href)
      return url.origin !== window.location.origin
    } catch (_e) {
      return false
    }
  }
}
